import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  ScrollArea,
  Text,
  Title,
} from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import ReactJson from "react-json-view";
import { Clipboard, X } from "tabler-icons-react";
import moment from "moment";

function safeString(value) {
  if (value == null) return "-";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function formatTimestamp(value) {
  if (!value) return "-";
  const numeric = Number(value);
  const date =
    Number.isFinite(numeric) && `${Math.trunc(numeric)}`.length <= 10
      ? moment.unix(numeric)
      : moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss.SSS") : "-";
}

function normalizeLog(value = {}) {
  const level = (value.level || value.severity || "info").toLowerCase();
  const status =
    value.status ||
    value.status_code ||
    value.data?.status ||
    value.data?.status_code ||
    "";
  const request =
    value.request ||
    value.request_id ||
    value.traceId ||
    value.trace_id ||
    value.data?.request_id ||
    value.data?.method ||
    "-";

  return {
    level,
    status,
    request,
    category: value.category || value.service || "app",
    timestamp: value.timestamp,
    message: value.message || value.event || value.name || "No message",
    raw: value,
  };
}

function copyText(value) {
  if (!navigator?.clipboard || !value || value === "-") return;
  navigator.clipboard.writeText(String(value));
}

function DetailRow({ label, value, copy }) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <Group spacing={6} noWrap>
        <code>{safeString(value)}</code>
        {copy ? (
          <Button
            compact
            variant="subtle"
            className="detail-copy"
            onClick={() => copyText(value)}
          >
            <Clipboard size={13} />
          </Button>
        ) : null}
      </Group>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="detail-section">
      <Text className="detail-section__title">{title}</Text>
      {children}
    </section>
  );
}

const CodeDrawer = ({ opened, jsonValue, handleOnClose }) => {
  const { height } = useViewportSize();
  const entry = normalizeLog(jsonValue);
  const isError = entry.level === "error" || entry.level === "fatal";

  return (
    <Drawer
      opened={opened}
      onClose={handleOnClose}
      size="min(620px, 92vw)"
      position="right"
      overlayOpacity={0.25}
      lockScroll
      withCloseButton={false}
      withinPortal={false}
      classNames={{ drawer: "detail-drawer" }}
    >
      <div className="detail-drawer__header">
        <div>
          <Group spacing={8} noWrap>
            <span
              className={`log-dot ${
                isError
                  ? "log-dot--error"
                  : entry.level === "warn" || entry.level === "warning"
                  ? "log-dot--warn"
                  : "log-dot--info"
              }`}
            />
            <Text className={`detail-drawer__level ${isError ? "is-error" : ""}`}>
              {entry.level}
            </Text>
            <Title order={3} className="detail-drawer__title">
              {entry.request}
            </Title>
            {entry.status ? (
              <Badge className={`status-badge ${Number(entry.status) >= 400 ? "status-badge--error" : ""}`}>
                {entry.status}
              </Badge>
            ) : null}
          </Group>
          <Text className="detail-drawer__meta">
            {formatTimestamp(entry.timestamp)} · {entry.category}
          </Text>
        </div>
        <Button compact variant="subtle" className="icon-button" onClick={handleOnClose}>
          <X size={16} />
        </Button>
      </div>

      <ScrollArea style={{ height: height - 86 }}>
        <Box className="detail-drawer__body">
          <Section title="Raw JSON">
            <div className="detail-json">
              <ReactJson
                src={entry.raw}
                theme="monokai"
                iconStyle="triangle"
                indentWidth={4}
                collapsed={2}
                collapseStringsAfterLength={160}
                displayDataTypes={false}
                name={false}
              />
            </div>
          </Section>

          <Divider className="detail-divider" />

          <Section title="Message">
            <pre className="detail-message">{safeString(entry.message)}</pre>
          </Section>

          <Section title="Identifiers">
            <DetailRow label="Request" value={entry.request} copy />
            <DetailRow label="Category" value={entry.category} />
            <DetailRow label="Level" value={entry.level} />
            <DetailRow label="Timestamp" value={formatTimestamp(entry.timestamp)} copy />
            {entry.raw.traceId || entry.raw.trace_id ? (
              <DetailRow label="Trace" value={entry.raw.traceId || entry.raw.trace_id} copy />
            ) : null}
          </Section>

          {entry.raw.data ? (
            <Section title="Data">
              {Object.entries(entry.raw.data).map(([key, value]) => (
                <DetailRow key={key} label={key} value={value} copy={typeof value === "string"} />
              ))}
            </Section>
          ) : null}

        </Box>
      </ScrollArea>
    </Drawer>
  );
};

export default CodeDrawer;
